// AstriaPhilippinesV2Shell.tsx
import React from "react";
import { useLaneCopy } from "../../../utils/laneCopy";
import { useNavigate } from "react-router-dom";
import { Moon, Heart, Hourglass, CloudSun, Compass, NotebookPen } from "lucide-react";
import { PhilippinesV2Lang } from "./philippinesV2Language";
import { PHILIPPINES_V2_COPY } from "./philippinesV2Copy";


export const PHILIPPINES_V2_HEADING_FONT = "'Poppins', 'Inter', sans-serif";
export const PHILIPPINES_V2_BODY_FONT = "'Inter', sans-serif";
// Shared cap for header/tab-bar/body so the panel fills more of the chat
// column on wide screens instead of floating with large side margins.
const PHILIPPINES_V2_MAX_WIDTH = 960;

export const SHELL_COLORS_PHILIPPINES_V2 = {
  bg: "linear-gradient(160deg, #FFFBF9 0%, #FFF3EE 55%, #FFF8F0 100%)",
  card: "#FFFFFF",
  section: "#FFF1EC",
  cardBorder: "#FADCD2",
  titleColor: "#5B2A2E",
  sectionTitleColor: "#5B2A2E",
  bodyColor: "#6B3E3C",
  mutedColor: "#B08078",
  accent: "#E8695A",
  accentSoft: "rgba(232,105,90,0.12)",
  accentGlow: "0 0 0 3px rgba(232,105,90,0.16)",
  gold: "#E8A94B",
  goldSoft: "rgba(232,169,75,0.14)",
  rose: "#F2A0A8",
  white: "#FFFFFF",
};

export type AstriaPhilippinesV2Tab =
  | "soft_summary"
  | "connection_atmosphere"
  | "timing_rhythm"
  | "emotion_balance"
  | "guidance_path"
  | "your_note";

interface TabDef {
  key: AstriaPhilippinesV2Tab;
  dbName: string;
  icon: React.ReactNode;
  path: string;
  accent: [string, string];
}

// Icons/order per Vn.txt's PH v2 UI pack: 🌙 💗 ⏳ 🌤️ 🧭 📝
const TABS: TabDef[] = [
  {
    key: "soft_summary",
    dbName: "Soft Summary",
    icon: <Moon className="w-3.5 h-3.5" />,
    path: "/astria-philippines-v2/soft-summary",
    accent: ["#E8695A", "#F2A0A8"],
  },
  {
    key: "connection_atmosphere",
    dbName: "Connection Atmosphere",
    icon: <Heart className="w-3.5 h-3.5" />,
    path: "/astria-philippines-v2/connection-atmosphere",
    accent: ["#F2A0A8", "#E8A94B"],
  },
  {
    key: "timing_rhythm",
    dbName: "Timing Rhythm",
    icon: <Hourglass className="w-3.5 h-3.5" />,
    path: "/astria-philippines-v2/timing-rhythm",
    accent: ["#E8A94B", "#E8695A"],
  },
  {
    key: "emotion_balance",
    dbName: "Emotion Balance",
    icon: <CloudSun className="w-3.5 h-3.5" />,
    path: "/astria-philippines-v2/emotion-balance",
    accent: ["#F2A0A8", "#E8695A"],
  },
  {
    key: "guidance_path",
    dbName: "Guidance Path",
    icon: <Compass className="w-3.5 h-3.5" />,
    path: "/astria-philippines-v2/guidance-path",
    accent: ["#E8695A", "#E8A94B"],
  },
  {
    key: "your_note",
    dbName: "Your Note",
    icon: <NotebookPen className="w-3.5 h-3.5" />,
    path: "/astria-philippines-v2/your-note",
    accent: ["#E8A94B", "#F2A0A8"],
  },
];

interface AstriaPhilippinesV2ShellProps {
  active: AstriaPhilippinesV2Tab;
  lang: PhilippinesV2Lang;
  children: React.ReactNode;
}

const AstriaPhilippinesV2Shell: React.FC<AstriaPhilippinesV2ShellProps> = ({
  active,
  lang,
  children,
}) => {
  const navigate = useNavigate();
  const copy = useLaneCopy("v2", PHILIPPINES_V2_COPY, lang);

  return (
    <div
      className="min-h-screen"
      style={{
        background: SHELL_COLORS_PHILIPPINES_V2.bg,
        fontFamily: PHILIPPINES_V2_BODY_FONT,
      }}
    >
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          background: "rgba(255,251,249,0.82)",
          borderBottom: `1px solid ${SHELL_COLORS_PHILIPPINES_V2.cardBorder}`,
        }}
      >
        <div
          className="mx-auto flex items-center gap-3 px-5"
          style={{ maxWidth: PHILIPPINES_V2_MAX_WIDTH, paddingTop: 18, paddingBottom: 14 }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${SHELL_COLORS_PHILIPPINES_V2.accent} 0%, ${SHELL_COLORS_PHILIPPINES_V2.gold} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 14px ${SHELL_COLORS_PHILIPPINES_V2.accentSoft}`,
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 16, color: "#fff" }}>💗</span>
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: SHELL_COLORS_PHILIPPINES_V2.titleColor,
                fontFamily: PHILIPPINES_V2_HEADING_FONT,
                letterSpacing: "0.01em",
              }}
            >
              {copy.shell.title}
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: SHELL_COLORS_PHILIPPINES_V2.mutedColor,
              }}
            >
              {copy.shell.subtitle}
            </div>
          </div>

        </div>

        {/* ─── Tab bar ─────────────────────────────────────────────────
            Tabs stretch (flex: 1) to fill the full row width evenly — no
            leftover space on the right. `flex-wrap` + `minWidth` is the
            fallback for any viewport too narrow to fit all 6 on one line,
            so it wraps to a 2nd row instead of clipping/scrolling. */}
        <div
          className="mx-auto px-5 pb-3"
          style={{
            maxWidth: PHILIPPINES_V2_MAX_WIDTH,
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
          }}
        >
          {TABS.map((tab) => {
            const isActive = tab.key === active;
            return (
              <button
                key={tab.key}
                onClick={() => navigate(tab.path)}
                className="flex items-center justify-center gap-1 rounded-full transition-all whitespace-nowrap"
                style={{
                  flex: "1 1 auto",
                  minWidth: "fit-content",
                  paddingInline: 10,
                  paddingBlock: 7,
                  fontSize: 11,
                  fontWeight: 600,
                  color: isActive
                    ? "#FFFFFF"
                    : SHELL_COLORS_PHILIPPINES_V2.bodyColor,
                  background: isActive
                    ? `linear-gradient(135deg, ${tab.accent[0]} 0%, ${tab.accent[1]} 100%)`
                    : SHELL_COLORS_PHILIPPINES_V2.section,
                  boxShadow: isActive
                    ? `0 4px 14px ${SHELL_COLORS_PHILIPPINES_V2.accentSoft}`
                    : "none",
                  border: isActive
                    ? "none"
                    : `1px solid ${SHELL_COLORS_PHILIPPINES_V2.cardBorder}`,
                }}
              >
                {tab.icon}
                <span>{copy.tabBar[tab.key].label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── Body ────────────────────────────────────────────────────── */}
      <div
        className="mx-auto px-4 sm:px-5"
        style={{ maxWidth: PHILIPPINES_V2_MAX_WIDTH, paddingTop: 24, paddingBottom: 64 }}
      >
        {children}
      </div>
    </div>
  );
};

export default AstriaPhilippinesV2Shell;
export { TABS as ASTRIA_PHILIPPINES_V2_TABS };
